import { useState } from "react";
import { PageHeader, Panel } from "@/components/admin/shared";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";
import { FileText, FileSpreadsheet, FileType2, Loader2, Eye } from "lucide-react";
import { ReportViewer } from "@/components/admin/ReportViewer";
import { toast } from "sonner";

const REPORTS = [
  { type: "visitors", title: "Daily / Weekly / Monthly Visitor Report", desc: "All visits with source, purpose and QR entry point" },
  { type: "leads", title: "Lead Report", desc: "Full lead pipeline with interests and status" },
  { type: "conversions", title: "Conversion Report", desc: "Memberships sold, value and sales executive" },
  { type: "source", title: "Marketing Source Report", desc: "Visitors, leads and conversions by marketing source" },
  { type: "walking", title: "Walking Visitor Report", desc: "Dedicated report for walking-gate visitors" },
];

const FORMATS = [
  { key: "pdf", label: "PDF", icon: FileType2, cls: "bg-[#081226] hover:bg-[#0f2042] text-white" },
  { key: "xlsx", label: "Excel", icon: FileSpreadsheet, cls: "bg-emerald-600 hover:bg-emerald-700 text-white" },
  { key: "csv", label: "CSV", icon: FileText, cls: "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50" },
];

export default function Reports() {
  const [busy, setBusy] = useState(null);
  const [viewing, setViewing] = useState(null);

  const download = async (type, format) => {
    const key = `${type}-${format}`;
    setBusy(key);
    try {
      const res = await fetch(`${API}/reports/${type}?format=${format}`, { credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename=([^;]+)/);
      const name = m ? m[1].trim() : `Oneiros_${type}.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error("Could not generate report");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <PageHeader title="Reports & Export" subtitle="View reports on screen or download as PDF, Excel or CSV" />
      <div className="grid sm:grid-cols-2 gap-5">
        {REPORTS.map((r) => (
          <Panel key={r.type} data-testid={`report-${r.type}`}>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg,#0f2042,#081226)" }}>
                <FileSpreadsheet size={20} className="text-[#E8752A]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-bold text-[#081226]">{r.title}</h3>
                <p className="text-sm text-slate-500 mt-1">{r.desc}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button onClick={() => setViewing(r.type)} data-testid={`view-${r.type}-btn`} className="h-9 rounded-lg px-3 btn-orange"><Eye size={15} /> View</Button>
                  {FORMATS.map((f) => {
                    const key = `${r.type}-${f.key}`;
                    const Icon = f.icon;
                    return (
                      <Button key={f.key} onClick={() => download(r.type, f.key)} disabled={busy === key}
                        data-testid={`download-${r.type}-${f.key}-btn`}
                        className={`h-9 rounded-lg px-3 ${f.cls}`}>
                        {busy === key ? <Loader2 size={15} className="animate-spin" /> : <Icon size={15} />} {f.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
          </Panel>
        ))}
      </div>
      <ReportViewer type={viewing} onClose={() => setViewing(null)} />
      <p className="text-xs text-slate-400 mt-5">PDF and Excel exports are fully branded with the Oneiros Varachha identity. CSV opens directly in Excel and Google Sheets.</p>
    </div>
  );
}
